import { useEffect, useState } from "react";
import api, { fmtPKR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, Plus, Pencil, Trash2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "../lib/api";
import PageShell from "../components/PageShell";

const empty = {
  name: "",
  brand: "",
  purchase_cost: 0,
  sale_price: 0,
  current_stock: 0,
  minimum_stock: 0,
  product_code: "",
  barcode: "",
  sets: 0,
  pieces: 0,
  codeMode: "auto",
};

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | low
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const res = await api.get("/products", {
      params: { search: search || undefined, low_stock: filter === "low" || undefined },
    });
    setProducts(res.data);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filter]);

  const openCreate = () => {
    setForm(empty);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      brand: p.brand || "",
      purchase_cost: p.purchase_cost,
      sale_price: p.sale_price,
      current_stock: p.current_stock,
      minimum_stock: p.minimum_stock,
      product_code: p.product_code,
      barcode: p.barcode || "",
      sets: p.sets || 0,
      pieces: p.pieces || 0,
      codeMode: "manual",
    });
    setEditingId(p.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const payload = {
      name: form.name,
      brand: form.brand,
      purchase_cost: Number(form.purchase_cost) || 0,
      sale_price: Number(form.sale_price) || 0,
      current_stock: Number(form.current_stock) || 0,
      minimum_stock: Number(form.minimum_stock) || 0,
      product_code: form.codeMode === "manual" && form.product_code ? form.product_code : null,
      barcode: form.barcode || "",
      sets: Number(form.sets) || 0,
      pieces: Number(form.pieces) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, { ...payload, product_code: form.product_code });
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const exportXLSX = async () => {
    try {
      await downloadFile("/export/inventory", "inventory.xlsx");
    } catch {
      toast.error("Export failed");
    }
  };

  const lowCount = products.filter((p) => p.current_stock <= p.minimum_stock).length;

  return (
    <PageShell
      ticker={[
        { label: "Console", value: "INVENTORY", live: true },
        { label: "SKUs", value: String(products.length).padStart(3, "0"), mono: true },
        { label: "Low stock", value: String(lowCount).padStart(2, "0"), mono: true },
        { label: "Currency", value: "PKR ₨" },
      ]}
      eyebrow="Chapter · Parts & stock"
      title={<>Parts &amp; <em className="italic text-blue-700">stock</em></>}
      description="Every SKU on the shelves, priced and counted. Search, filter and reorder."
      colophon="Inventory · Section III"
      cta={
        <div className="flex items-center gap-2" data-testid="inventory-page">
          <Button variant="outline" onClick={exportXLSX} data-testid="inventory-export-btn">
            <Download size={16} className="mr-2" /> Export Excel
          </Button>
          <Button
            onClick={openCreate}
            data-testid="inventory-add-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={16} className="mr-2" /> Add Product
          </Button>
        </div>
      }
    >
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, brand…"
            data-testid="inventory-search-input"
            className="pl-9"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="inventory-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="low">Low Stock Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Brand</th>
              <th className="px-4 py-3 text-right">Sets</th>
              <th className="px-4 py-3 text-right">Pieces</th>
              <th className="px-4 py-3 text-right">Purchase</th>
              <th className="px-4 py-3 text-right">Sale</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Min</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr
                key={p.id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${
                  p.current_stock <= p.minimum_stock ? "row-low-stock" : ""
                }`}
                data-testid={`inventory-row-${p.product_code}`}
              >
                <td className="px-4 py-2 font-mono-tabular text-xs">{p.product_code}</td>
                <td className="px-4 py-2 text-slate-900">{p.name}</td>
                <td className="px-4 py-2 text-slate-500">{p.brand || "-"}</td>
                <td className="px-4 py-2 text-right tabular text-slate-700">{p.sets || 0}</td>
                <td className="px-4 py-2 text-right tabular text-slate-700">{p.pieces || 0}</td>
                <td className="px-4 py-2 text-right tabular text-slate-700">{fmtPKR(p.purchase_cost)}</td>
                <td className="px-4 py-2 text-right tabular text-slate-900 font-medium">{fmtPKR(p.sale_price)}</td>
                <td className="px-4 py-2 text-right tabular">
                  <span className={p.current_stock <= p.minimum_stock ? "text-red-700 font-semibold inline-flex items-center gap-1" : "text-slate-700"}>
                    {p.current_stock <= p.minimum_stock && <AlertTriangle size={12} />}
                    {p.current_stock}
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular text-slate-500">{p.minimum_stock}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`inventory-edit-${p.product_code}`}
                    className="text-slate-500 hover:text-blue-700 mr-3"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => del(p)}
                    data-testid={`inventory-delete-${p.product_code}`}
                    className="text-slate-500 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm">
                  No products. Click "Add Product" to start.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="inventory-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Product Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label>Brand</Label>
              <Input
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                data-testid="product-brand-input"
              />
            </div>
            <div>
              <Label>Product Code</Label>
              {!editingId ? (
                <div className="flex gap-2">
                  <Select
                    value={form.codeMode}
                    onValueChange={(v) => setForm({ ...form, codeMode: v })}
                  >
                    <SelectTrigger className="w-32" data-testid="product-code-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    disabled={form.codeMode === "auto"}
                    value={form.codeMode === "auto" ? "" : form.product_code}
                    placeholder={form.codeMode === "auto" ? "Auto-generated" : "Enter code"}
                    onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                    data-testid="product-code-input"
                  />
                </div>
              ) : (
                <Input
                  value={form.product_code}
                  onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                  data-testid="product-code-input"
                />
              )}
            </div>
            <div>
              <Label>Purchase Cost (₨)</Label>
              <Input
                type="number"
                value={form.purchase_cost}
                onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })}
                data-testid="product-purchase-input"
              />
            </div>
            <div>
              <Label>Sale Price (₨)</Label>
              <Input
                type="number"
                value={form.sale_price}
                onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                data-testid="product-sale-input"
              />
            </div>
            <div>
              <Label>Current Stock</Label>
              <Input
                type="number"
                value={form.current_stock}
                onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                data-testid="product-stock-input"
              />
            </div>
            <div>
              <Label>Minimum Stock</Label>
              <Input
                type="number"
                value={form.minimum_stock}
                onChange={(e) => setForm({ ...form, minimum_stock: e.target.value })}
                data-testid="product-min-input"
              />
            </div>
            <div>
              <Label>Sets</Label>
              <Input
                type="number"
                min="0"
                value={form.sets}
                onChange={(e) => setForm({ ...form, sets: e.target.value })}
                data-testid="product-sets-input"
                placeholder="0"
              />
            </div>
            <div>
              <Label>Pieces</Label>
              <Input
                type="number"
                min="0"
                value={form.pieces}
                onChange={(e) => setForm({ ...form, pieces: e.target.value })}
                data-testid="product-pieces-input"
                placeholder="0"
              />
            </div>
            <div className="col-span-2">
              <Label>Barcode (optional)</Label>
              <Input
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                placeholder="Scan or type barcode"
                data-testid="product-barcode-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                If set, the POS will auto-add this item when you scan its barcode.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="product-save-btn">
              {editingId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
